import { useState, useMemo, useEffect } from 'react'
import { useQueryClient, useMutation, useQuery } from '@tanstack/react-query'
import { useAuthStore } from '@/store/authStore'
import { useNavigate, useSearchParams } from 'react-router-dom'
import toast from 'react-hot-toast'
import {
  Radio, CheckCircle2, QrCode, AlertCircle, RefreshCw, Trash2,
  Plus, Search, X, MessageSquare, Send, Globe, Copy, Check,
  Code2, Server, PowerOff, ShieldCheck, ShieldAlert, Layers, Pencil, Phone
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import Modal from '@/components/ui/modal'
import CreateChannelInstanceModal from './CreateWahaInstanceModal'
import EditChannelInstanceModal from './EditChannelInstanceModal'
import ChannelQrModal from './WahaQrModal'
import {
  listChannelInstances,
  deleteChannelInstance,
  reconnectChannelInstance,
  type ChannelInstanceResponse
} from '@/services/channelsApi'

const CHANNEL_META: Record<
  string,
  { bg: string; icon: React.ComponentType<{ className?: string }>; label: string; text: string }
> = {
  WHATSAPP: {
    bg: 'bg-emerald-50 border-emerald-200 text-[#075E54]',
    icon: MessageSquare,
    label: 'WhatsApp (WAHA)',
    text: 'text-[#075E54]',
  },
  WHATSAPP_CLOUD: {
    bg: 'bg-teal-50 border-teal-200 text-teal-800',
    icon: MessageSquare,
    label: 'WhatsApp Cloud API',
    text: 'text-teal-800',
  },
  TELEGRAM: {
    bg: 'bg-sky-50 border-sky-200 text-sky-600',
    icon: Send,
    label: 'Telegram Bot',
    text: 'text-sky-600',
  },
  WEBCHAT: {
    bg: 'bg-purple-50 border-purple-200 text-purple-600',
    icon: Globe,
    label: 'WebChat Widget',
    text: 'text-purple-600',
  },
  MESSENGER: {
    bg: 'bg-blue-50 border-blue-200 text-blue-600',
    icon: MessageSquare,
    label: 'Messenger',
    text: 'text-blue-600',
  },
  INSTAGRAM: {
    bg: 'bg-pink-50 border-pink-200 text-pink-600',
    icon: MessageSquare,
    label: 'Instagram Direct',
    text: 'text-pink-600',
  },
}

type StatusKey = 'CONNECTED' | 'CONNECTING' | 'PENDING' | 'DISCONNECTED' | 'ERROR' | 'CREATED'

const STATUS_CONFIG: Record<
  StatusKey,
  { label: string; dot: string; badge: string; icon: React.ComponentType<{ className?: string }> }
> = {
  CONNECTED: {
    label: 'Conectado',
    dot: 'bg-emerald-500 animate-pulse',
    badge: 'text-emerald-800 bg-emerald-50 border-emerald-200',
    icon: CheckCircle2,
  },
  CONNECTING: {
    label: 'Conectando...',
    dot: 'bg-amber-500 animate-pulse',
    badge: 'text-amber-800 bg-amber-50 border-amber-200',
    icon: RefreshCw,
  },
  PENDING: {
    label: 'Pendiente QR',
    dot: 'bg-amber-400',
    badge: 'text-amber-800 bg-amber-50 border-amber-200',
    icon: QrCode,
  },
  DISCONNECTED: {
    label: 'Desconectado',
    dot: 'bg-slate-400',
    badge: 'text-slate-600 bg-slate-50 border-slate-200',
    icon: PowerOff,
  },
  ERROR: {
    label: 'Error',
    dot: 'bg-rose-500',
    badge: 'text-rose-700 bg-rose-50 border-rose-200',
    icon: AlertCircle,
  },
  CREATED: {
    label: 'Creado',
    dot: 'bg-slate-300',
    badge: 'text-slate-500 bg-slate-50 border-slate-200',
    icon: Server,
  },
}

export default function InstancesList() {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const tenantId = useAuthStore((s) => s.tenantId)
  const qc = useQueryClient()

  // State
  const shouldOpenCreate = searchParams.get('create') === 'true' || searchParams.get('action') === 'connect'
  const [showCreate, setShowCreate] = useState(shouldOpenCreate)
  const [showQr, setShowQr] = useState<{ id: string; name: string } | null>(null)
  const [instanceToEdit, setInstanceToEdit] = useState<ChannelInstanceResponse | null>(null)
  const [instanceToDelete, setInstanceToDelete] = useState<ChannelInstanceResponse | null>(null)
  const [reconnectingId, setReconnectingId] = useState<string | null>(null)
  const [search, setSearch] = useState('')
  const [channelFilter, setChannelFilter] = useState<string>('ALL')
  const [copiedKey, setCopiedKey] = useState<string | null>(null)

  useEffect(() => {
    if (shouldOpenCreate) {
      setShowCreate(true)
    }
  }, [shouldOpenCreate])

  const handleCloseCreate = () => {
    setShowCreate(false)
    if (searchParams.get('create') || searchParams.get('action')) {
      const next = new URLSearchParams(searchParams)
      next.delete('create')
      next.delete('action')
      setSearchParams(next, { replace: true })
    }
  }

  const { data: instances = [], isLoading, refetch, isFetching } = useQuery({
    queryKey: ['channel-instances', tenantId],
    queryFn: listChannelInstances,
    enabled: !!tenantId,
    refetchInterval: 15_000,
  })

  const deleteMut = useMutation({
    mutationFn: deleteChannelInstance,
    onSuccess: () => {
      toast.success('Canal eliminado exitosamente')
      qc.invalidateQueries({ queryKey: ['channel-instances'] })
      setInstanceToDelete(null)
    },
    onError: () => toast.error('Error al eliminar el canal'),
  })

  const reconnectMut = useMutation({
    mutationFn: reconnectChannelInstance,
    onSuccess: () => {
      toast.success('Reconexión iniciada')
      qc.invalidateQueries({ queryKey: ['channel-instances'] })
      setReconnectingId(null)
    },
    onError: () => {
      toast.error('Error al reconectar el canal')
      setReconnectingId(null)
    },
  })

  const handleCreated = (instanceId: string, channelType: string) => {
    handleCloseCreate()
    qc.invalidateQueries({ queryKey: ['channel-instances'] })
    if (channelType === 'WHATSAPP') {
      const inst = instances.find((i) => i.id === instanceId)
      setShowQr({ id: instanceId, name: inst?.instanceName || instanceId })
    } else {
      toast.success('Canal creado exitosamente')
    }
  }

  const handleCopyText = (text: string, key: string) => {
    if (typeof navigator !== 'undefined' && navigator.clipboard) {
      navigator.clipboard.writeText(text).catch(() => {})
    }
    setCopiedKey(key)
    toast.success('Copiado al portapapeles')
    setTimeout(() => setCopiedKey(null), 2000)
  }

  // Filter instances
  const filtered = useMemo(() => {
    return instances.filter((inst) => {
      const matchesSearch =
        inst.instanceName.toLowerCase().includes(search.toLowerCase()) ||
        inst.id.toLowerCase().includes(search.toLowerCase()) ||
        (inst.config?.phoneNumberId && String(inst.config.phoneNumberId).includes(search))

      const matchesType =
        channelFilter === 'ALL'
          ? true
          : channelFilter === 'WHATSAPP'
          ? inst.channelType.startsWith('WHATSAPP')
          : inst.channelType === channelFilter

      return matchesSearch && matchesType
    })
  }, [instances, search, channelFilter])

  // Metrics
  const activeCount = instances.filter((i) => i.status === 'CONNECTED').length
  const pendingCount = instances.filter((i) => i.status === 'PENDING' || i.status === 'CONNECTING').length
  const issuesCount = instances.filter((i) => i.status === 'ERROR' || i.status === 'DISCONNECTED').length

  const waCount = instances.filter((i) => i.channelType.startsWith('WHATSAPP')).length
  const tgCount = instances.filter((i) => i.channelType === 'TELEGRAM').length
  const webCount = instances.filter((i) => i.channelType === 'WEBCHAT').length

  return (
    <div className="space-y-6 pb-14">
      {/* Hero Section */}
      <section className="overflow-hidden rounded-[2rem] border border-emerald-100 bg-gradient-to-br from-emerald-50 via-white to-teal-50 shadow-sm">
        <div className="grid gap-6 px-5 py-6 lg:grid-cols-[1.4fr_0.8fr] lg:px-8 lg:py-8">
          <div className="space-y-4">
            <div className="inline-flex items-center gap-2 rounded-full bg-emerald-100 px-3 py-1 text-xs font-semibold uppercase tracking-[0.2em] text-[#075E54]">
              <Radio className="h-3.5 w-3.5 animate-pulse text-[#075E54]" />
              Canales & Conexiones
            </div>
            <div>
              <h1 data-tour="channels-main" className="text-3xl font-black tracking-tight text-slate-900 lg:text-4xl">
                Instancias y Canales
              </h1>
              <p className="mt-3 max-w-2xl text-sm leading-6 text-slate-600 lg:text-base">
                Conecta y supervisa tus líneas de WhatsApp Business Cloud API (Oficial de Meta), Telegram y WebChat. Tu bot responderá en tiempo real a tus clientes a través de estas conexiones.
              </p>
            </div>

            <div className="flex flex-wrap gap-3">
              <Button
                type="button"
                onClick={() => setShowCreate(true)}
                disabled={!tenantId}
                className="inline-flex items-center gap-2 rounded-full bg-[#075E54] px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-[#064E46]"
              >
                <Plus className="h-4 w-4" />
                Conectar canal
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => refetch()}
                disabled={isFetching}
                className="inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-white px-4 py-2.5 text-sm font-semibold text-emerald-800 transition-colors hover:bg-emerald-50"
              >
                <RefreshCw className={`h-4 w-4 ${isFetching ? 'animate-spin' : ''}`} />
                Refrescar
              </Button>
            </div>

            {/* Metric cards */}
            <div className="grid gap-3 sm:grid-cols-4">
              <MetricCard label="Total" value={String(instances.length)} icon={Layers} />
              <MetricCard label="En línea" value={String(activeCount)} icon={CheckCircle2} tone="emerald" />
              <MetricCard label="Pendientes" value={String(pendingCount)} icon={QrCode} tone="amber" />
              <MetricCard label="Inactivos" value={String(issuesCount)} icon={AlertCircle} tone="slate" />
            </div>
          </div>

          {/* Quick tips panel */}
          <div className="rounded-[1.75rem] border border-white/70 bg-white/80 p-4 shadow-lg backdrop-blur">
            <div className="flex items-center justify-between border-b border-slate-100 pb-3">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">Estado de red</p>
                <p className="mt-1 text-sm font-semibold text-slate-900">
                  {activeCount} {activeCount === 1 ? 'canal activo' : 'canales activos'}
                </p>
              </div>
              <div className="rounded-full bg-emerald-100 px-3 py-1 text-xs font-semibold text-[#075E54] flex items-center gap-1.5">
                <ShieldCheck className="w-3.5 h-3.5" />
                Aislado por Tenant
              </div>
            </div>

            <div className="mt-4 space-y-3">
              <p className="text-sm text-slate-600">
                Las sesiones de mensajería están cifradas y sincronizadas automáticamente con la bandeja de conversaciones.
              </p>
              <div className="rounded-2xl bg-slate-950 p-4 text-white">
                <p className="text-xs uppercase tracking-[0.2em] text-white/50">Recomendaciones</p>
                <ul className="mt-3 space-y-2 text-sm text-white/85">
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-400" />
                    Conecta tu cuenta de WhatsApp Business mediante el Registro Insertado Oficial de Meta.
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-400" />
                    Usa WhatsApp Cloud API para volúmenes comerciales empresariales altos.
                  </li>
                  <li className="flex items-start gap-2">
                    <RefreshCw className="mt-0.5 h-4 w-4 shrink-0 text-emerald-400" />
                    Si un canal se desconecta, pulsa "Reconectar" para reanudar la sesión.
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Toolbar & Filter Tabs */}
      <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm flex flex-col md:flex-row md:items-center justify-between gap-3">
        {/* Search */}
        <div className="relative flex-1 max-w-md">
          <Search className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar por nombre o ID de instancia..."
            className="pl-10 pr-9 rounded-xl border-slate-200 text-sm h-10"
          />
          {search && (
            <button
              onClick={() => setSearch('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 p-0.5 rounded-full"
              title="Limpiar búsqueda"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>

        {/* Channel type tabs */}
        <div className="flex items-center gap-1.5 overflow-x-auto pb-1 md:pb-0 scrollbar-none">
          <button
            onClick={() => setChannelFilter('ALL')}
            className={`px-3 py-1.5 rounded-xl text-xs font-semibold whitespace-nowrap transition-colors ${
              channelFilter === 'ALL'
                ? 'bg-[#075E54] text-white shadow-xs'
                : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
            }`}
          >
            Todos ({instances.length})
          </button>
          <button
            onClick={() => setChannelFilter('WHATSAPP')}
            className={`px-3 py-1.5 rounded-xl text-xs font-semibold whitespace-nowrap transition-colors ${
              channelFilter === 'WHATSAPP'
                ? 'bg-[#075E54] text-white shadow-xs'
                : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
            }`}
          >
            WhatsApp ({waCount})
          </button>
          <button
            onClick={() => setChannelFilter('TELEGRAM')}
            className={`px-3 py-1.5 rounded-xl text-xs font-semibold whitespace-nowrap transition-colors ${
              channelFilter === 'TELEGRAM'
                ? 'bg-[#075E54] text-white shadow-xs'
                : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
            }`}
          >
            Telegram ({tgCount})
          </button>
          <button
            onClick={() => setChannelFilter('WEBCHAT')}
            className={`px-3 py-1.5 rounded-xl text-xs font-semibold whitespace-nowrap transition-colors ${
              channelFilter === 'WEBCHAT'
                ? 'bg-[#075E54] text-white shadow-xs'
                : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
            }`}
          >
            WebChat ({webCount})
          </button>
        </div>
      </section>

      {/* Instances Grid / List */}
      {isLoading ? (
        <section className="grid gap-4 sm:grid-cols-2">
          {Array.from({ length: 4 }).map((_, index) => (
            <div key={index} className="animate-pulse rounded-2xl border border-slate-200 bg-white p-5 shadow-sm space-y-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-slate-200 shrink-0" />
                <div className="space-y-2 flex-1">
                  <div className="h-4 w-36 rounded bg-slate-200" />
                  <div className="h-3 w-20 rounded bg-slate-100" />
                </div>
              </div>
              <div className="h-10 rounded-xl bg-slate-100" />
            </div>
          ))}
        </section>
      ) : filtered.length === 0 ? (
        <section className="rounded-2xl border border-dashed border-slate-200 bg-white p-12 text-center shadow-sm">
          <div className="w-16 h-16 rounded-2xl bg-emerald-50 text-[#075E54] flex items-center justify-center mx-auto mb-4">
            <Radio className="h-8 w-8 text-[#075E54]" />
          </div>
          <h2 className="text-xl font-bold text-slate-900">
            {search || channelFilter !== 'ALL' ? 'Sin resultados para los filtros' : 'Sin canales configurados'}
          </h2>
          <p className="text-sm text-slate-500 mt-1 max-w-md mx-auto">
            {search || channelFilter !== 'ALL'
              ? 'Prueba modificando el término de búsqueda o selecciona otra categoría de canal.'
              : 'Conecta tu primer canal de mensajería para que tus clientes puedan interactuar y comprar a través del bot.'}
          </p>
          <div className="mt-5 flex justify-center gap-3">
            {search || channelFilter !== 'ALL' ? (
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setSearch('')
                  setChannelFilter('ALL')
                }}
                className="rounded-xl"
              >
                Limpiar filtros
              </Button>
            ) : (
              <Button
                type="button"
                onClick={() => setShowCreate(true)}
                className="bg-[#075E54] hover:bg-[#064E46] text-white rounded-xl shadow-sm px-5"
              >
                <Plus className="w-4 h-4 mr-1.5" /> Conectar primer canal
              </Button>
            )}
          </div>
        </section>
      ) : (
        <section className="grid gap-4 sm:grid-cols-2">
          {filtered.map((inst: ChannelInstanceResponse) => {
            const ch = CHANNEL_META[inst.channelType] || {
              bg: 'bg-slate-50 border-slate-200 text-slate-700',
              icon: Server,
              label: inst.channelType,
              text: 'text-slate-700',
            }
            const ChannelIcon = ch.icon
            const isConnected =
              inst.status === 'CONNECTED' &&
              inst.metadata?.phoneStatus !== 'DISCONNECTED' &&
              inst.metadata?.phoneStatus !== 'PENDING'
            const isMetaUnverified =
              inst.channelType === 'WHATSAPP_CLOUD' &&
              !isConnected &&
              (inst.metadata?.codeVerificationStatus === 'NOT_VERIFIED' ||
                inst.metadata?.phoneStatus === 'DISCONNECTED' ||
                inst.metadata?.phoneStatus === 'PENDING')
            const stKey = isMetaUnverified
              ? 'PENDING'
              : (inst.status as StatusKey) in STATUS_CONFIG
              ? (inst.status as StatusKey)
              : 'CREATED'
            const st = STATUS_CONFIG[stKey]
            const StatusIcon = st.icon

            const isPending = inst.status === 'PENDING' || inst.status === 'CONNECTING'
            const isError = inst.status === 'ERROR'
            const isReconnectingThis = reconnectingId === inst.id && reconnectMut.isPending

            return (
              <article
                key={inst.id}
                className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm transition-all hover:shadow-md flex flex-col justify-between"
              >
                <div>
                  {/* Card Header */}
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className={`w-12 h-12 rounded-2xl border flex items-center justify-center shrink-0 ${ch.bg}`}>
                        <ChannelIcon className="w-6 h-6" />
                      </div>
                      <div className="min-w-0">
                        <h3 className="font-bold text-slate-900 text-base leading-tight truncate">
                          {inst.instanceName}
                        </h3>
                        <div className="flex items-center gap-2 mt-1">
                          <span className="text-xs text-slate-400 font-mono">
                            ID: {inst.id.slice(0, 10)}...
                          </span>
                          <button
                            type="button"
                            onClick={() => handleCopyText(inst.id, `id-${inst.id}`)}
                            className="text-slate-400 hover:text-slate-700 p-0.5 rounded transition-colors"
                            title="Copiar ID de instancia"
                          >
                            {copiedKey === `id-${inst.id}` ? (
                              <Check className="w-3.5 h-3.5 text-emerald-600" />
                            ) : (
                              <Copy className="w-3.5 h-3.5" />
                            )}
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* Status Badge */}
                    <span
                      className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold border shrink-0 ${st.badge}`}
                    >
                      <StatusIcon className="w-3.5 h-3.5" />
                      {isMetaUnverified ? 'Pendiente QR Meta' : st.label}
                    </span>
                  </div>

                  {/* Channel Tag */}
                  <div className="mt-3 flex items-center gap-2 flex-wrap">
                    <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-slate-100 text-slate-700">
                      {ch.label}
                    </span>
                    {inst.channelType === 'WHATSAPP_CLOUD' && (
                      <span
                        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold border ${
                          inst.metadata?.isOnBizApp
                            ? 'bg-emerald-50 text-emerald-800 border-emerald-200'
                            : 'bg-slate-100 text-slate-600 border-slate-200'
                        }`}
                      >
                        {inst.metadata?.isOnBizApp ? 'Modo Coexistencia (App + Cloud)' : 'Modo Cloud Estándar'}
                      </span>
                    )}
                    {inst.channelType === 'WHATSAPP_CLOUD' && inst.metadata?.hasPaymentMethod === false && (
                      <span
                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold border bg-amber-50 text-amber-800 border-amber-200"
                        title="Falta método de pago en Meta Business Suite"
                      >
                        <AlertCircle className="w-3 h-3 text-amber-600" />
                        Falta método de pago en Meta
                      </span>
                    )}
                    {inst.config?.phoneNumberId && (
                      <span className="text-xs text-slate-500 font-mono bg-slate-50 border border-slate-100 px-2 py-0.5 rounded">
                        PhoneID: {inst.config.phoneNumberId}
                      </span>
                    )}
                    {inst.metadata?.displayPhoneNumber && (
                      <span className="text-xs text-emerald-700 font-medium bg-emerald-50 border border-emerald-100 px-2 py-0.5 rounded flex items-center gap-1">
                        <Phone className="w-3 h-3 text-emerald-600" />
                        {inst.metadata.displayPhoneNumber}
                      </span>
                    )}
                    {inst.metadata?.verifiedName && (
                      <span className="text-xs text-slate-600 font-medium bg-slate-50 border border-slate-100 px-2 py-0.5 rounded">
                        {inst.metadata.verifiedName}
                      </span>
                    )}
                    {inst.metadata?.telegramBotUsername && (
                      <span className="text-xs text-sky-600 font-medium bg-sky-50 border border-sky-100 px-2 py-0.5 rounded">
                        @{inst.metadata.telegramBotUsername}
                      </span>
                    )}
                  </div>

                  {/* Contingency / Failover Alert */}
                  {inst.metadata?.nodeFailover?.active && (
                    <div
                      role="status"
                      aria-live="polite"
                      className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-xl flex items-start gap-2.5 text-xs text-amber-900 shadow-xs"
                    >
                      <ShieldAlert className="w-4 h-4 text-amber-600 shrink-0 mt-0.5 animate-pulse" />
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="font-bold text-amber-950">Modo Contingencia Activo</p>
                          <span className="inline-flex items-center px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider bg-amber-200 text-amber-900 rounded-md">
                            Pool Iridia
                          </span>
                        </div>
                        <p className="mt-1 text-amber-800 leading-relaxed">
                          {inst.metadata.nodeFailover.bannerMessage ||
                            'Tu Iridia Node físico está desconectado. Tus ventas siguen activas mediante el pool de contingencia de la plataforma.'}
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Error Alert */}
                  {isError && (
                    <div className="mt-3 p-3 bg-rose-50 border border-rose-200 rounded-xl flex items-start gap-2 text-xs text-rose-700">
                      <AlertCircle className="w-4 h-4 text-rose-600 shrink-0 mt-0.5" />
                      <div>
                        <p className="font-semibold">Problema de conexión detectado</p>
                        <p className="mt-0.5 text-rose-600">
                          {inst.metadata?.lastError || 'Token inválido o expirado. Reconecta la instancia o verifica las credenciales.'}
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Meta Unverified / Coexistence QR Pending Alert */}
                  {isMetaUnverified && !isError && (
                    <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-xl flex items-start gap-2 text-xs text-amber-900">
                      <QrCode className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
                      <div>
                        <p className="font-bold">Número No Verificado / Pendiente de QR en Meta (#133010)</p>
                        <p className="mt-0.5 text-amber-800 leading-relaxed">
                          Meta reporta este número como <strong>{inst.metadata?.codeVerificationStatus || 'NOT_VERIFIED'}</strong> ({inst.metadata?.phoneStatus || 'DISCONNECTED'}). Pulsa <strong>Desconectar</strong> para revocar la sesión anterior y vuelve a vincular con una configuración de <em>Registro insertado de WhatsApp (Coexistencia)</em>.
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Webhook URL for Cloud API */}
                  {inst.channelType === 'WHATSAPP_CLOUD' && (
                    <div className="mt-3 p-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs text-slate-600 flex items-center justify-between gap-2">
                      <div className="truncate min-w-0">
                        <span className="font-semibold text-slate-700">Webhook:</span>{' '}
                        <code className="text-slate-800 font-mono text-[11px]">
                          {typeof window !== 'undefined' ? `${window.location.origin}/api/webhooks/meta` : '/api/webhooks/meta'}
                        </code>
                      </div>
                      <button
                        type="button"
                        onClick={() =>
                          handleCopyText(
                            `${window.location.origin}/api/webhooks/meta`,
                            `hook-${inst.id}`
                          )
                        }
                        className="text-slate-400 hover:text-slate-700 p-1 rounded transition-colors shrink-0"
                        title="Copiar URL del Webhook"
                      >
                        {copiedKey === `hook-${inst.id}` ? (
                          <Check className="w-3.5 h-3.5 text-emerald-600" />
                        ) : (
                          <Copy className="w-3.5 h-3.5" />
                        )}
                      </button>
                    </div>
                  )}
                </div>

                {/* Actions Footer */}
                <div className="mt-5 pt-3 border-t border-slate-100 flex items-center gap-2">
                  {/* QR button for pending WAHA (solo para WhatsApp Web Baileys / WAHA) */}
                  {inst.channelType === 'WHATSAPP' && isPending && (
                    <Button
                      type="button"
                      onClick={() => setShowQr({ id: inst.id, name: inst.instanceName })}
                      className="flex-1 bg-amber-500 hover:bg-amber-600 text-white rounded-xl text-xs font-bold gap-1.5 shadow-xs"
                    >
                      <QrCode className="w-4 h-4" />
                      Vincular QR
                    </Button>
                  )}

                  {/* WebChat setup button */}
                  {inst.channelType === 'WEBCHAT' && (
                    <Button
                      type="button"
                      onClick={() => navigate(`/app/channels/webchat/${inst.id}/setup`)}
                      className="flex-1 bg-[#075E54] hover:bg-[#064E46] text-white rounded-xl text-xs font-bold gap-1.5"
                    >
                      <Code2 className="w-4 h-4" />
                      Configurar Widget
                    </Button>
                  )}

                  {/* Reconnect button */}
                  {inst.channelType !== 'WEBCHAT' && !(inst.channelType === 'WHATSAPP' && isPending) && (
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        if (inst.channelType === 'WHATSAPP_CLOUD') {
                          setShowCreate(true)
                        } else {
                          setReconnectingId(inst.id)
                          reconnectMut.mutate(inst.id)
                        }
                      }}
                      disabled={isReconnectingThis}
                      className="flex-1 border-slate-200 text-slate-700 hover:bg-slate-50 rounded-xl text-xs font-semibold gap-1.5"
                    >
                      <RefreshCw className={`w-3.5 h-3.5 ${isReconnectingThis ? 'animate-spin' : ''}`} />
                      {isReconnectingThis
                        ? 'Reconectando...'
                        : inst.channelType === 'WHATSAPP_CLOUD'
                        ? (isMetaUnverified ? 'Vincular Meta / QR' : 'Reconectar Meta')
                        : 'Reconectar'}
                    </Button>
                  )}

                  {/* Edit button */}
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setInstanceToEdit(inst)}
                    className="border-slate-200 text-slate-700 hover:bg-slate-50 hover:text-emerald-800 rounded-xl px-3 py-2 text-xs font-semibold gap-1.5 shrink-0"
                    title="Editar datos de la instancia"
                  >
                    <Pencil className="w-3.5 h-3.5" />
                    <span>Editar</span>
                  </Button>

                  {/* Disconnect / Delete button */}
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setInstanceToDelete(inst)}
                    className="border-red-200 text-red-600 hover:bg-red-50 rounded-xl px-3 py-2 text-xs font-semibold gap-1.5 shrink-0"
                    title="Desconectar y eliminar instancia"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                    <span>Desconectar</span>
                  </Button>
                </div>
              </article>
            )
          })}
        </section>
      )}

      {/* Mobile Floating Action Button (FAB) */}
      {!isLoading && (
        <div className="sm:hidden fixed bottom-6 right-6 z-20">
          <button
            onClick={() => setShowCreate(true)}
            disabled={!tenantId}
            title="Conectar canal"
            className="w-14 h-14 bg-[#075E54] hover:bg-[#064E46] text-white rounded-full shadow-lg flex items-center justify-center transition-all hover:scale-105 active:scale-95 disabled:opacity-50"
          >
            <Plus className="w-6 h-6" />
          </button>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={Boolean(instanceToDelete)}
        onClose={() => !deleteMut.isPending && setInstanceToDelete(null)}
        title="Desconectar y Eliminar Canal"
        size="sm"
      >
        <div className="space-y-4">
          <div className="p-3.5 bg-red-50 border border-red-200 rounded-2xl flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-600 shrink-0 mt-0.5" />
            <div className="text-sm text-red-900">
              <p className="font-bold">¿Confirmas desconectar y eliminar este canal?</p>
              <p className="mt-1 text-xs text-red-700">
                Se desconectará la instancia <strong>"{instanceToDelete?.instanceName}"</strong> ({instanceToDelete?.channelType})
                {instanceToDelete?.channelType === 'WHATSAPP_CLOUD'
                  ? ' y se revocarán los permisos OAuth en Meta para que al volver a conectar se muestre el asistente completo y el código QR.'
                  : '. Las conversaciones entrantes de este canal ya no serán recibidas por el bot.'}
              </p>
            </div>
          </div>

          <div className="flex justify-end gap-2.5 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => setInstanceToDelete(null)}
              disabled={deleteMut.isPending}
              className="rounded-xl"
            >
              Cancelar
            </Button>
            <Button
              type="button"
              onClick={() => instanceToDelete && deleteMut.mutate(instanceToDelete.id)}
              disabled={deleteMut.isPending}
              className="bg-red-600 hover:bg-red-700 text-white font-semibold rounded-xl shadow-sm"
            >
              {deleteMut.isPending ? 'Desconectando...' : 'Desconectar y eliminar'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Modals for Create and QR */}
      {showCreate && (
        <CreateChannelInstanceModal
          onClose={handleCloseCreate}
          onSuccess={handleCreated}
        />
      )}

      {showQr && (
        <ChannelQrModal
          instanceId={showQr.id}
          instanceName={showQr.name}
          onClose={() => setShowQr(null)}
          onSuccess={() => {
            setShowQr(null)
            qc.invalidateQueries({ queryKey: ['channel-instances'] })
          }}
        />
      )}

      {instanceToEdit && (
        <EditChannelInstanceModal
          instance={instanceToEdit}
          onClose={() => setInstanceToEdit(null)}
          onSuccess={() => {
            setInstanceToEdit(null)
            qc.invalidateQueries({ queryKey: ['channel-instances', tenantId] })
          }}
        />
      )}
    </div>
  )
}

function MetricCard({
  label,
  value,
  icon: Icon,
  tone = 'slate',
}: {
  label: string
  value: string
  icon: React.ComponentType<{ className?: string }>
  tone?: 'emerald' | 'amber' | 'slate'
}) {
  const iconBg =
    tone === 'emerald'
      ? 'bg-emerald-50 text-[#075E54]'
      : tone === 'amber'
      ? 'bg-amber-50 text-amber-600'
      : 'bg-slate-50 text-slate-600'

  return (
    <div className="rounded-2xl border border-white/70 bg-white/80 p-4 shadow-sm backdrop-blur">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">{label}</p>
          <p className="mt-2 text-2xl font-black text-slate-900">{value}</p>
        </div>
        <div className={`rounded-2xl p-3 ${iconBg}`}>
          <Icon className="h-5 w-5" />
        </div>
      </div>
    </div>
  )
}
